if ( typeof CaloriesCalculator !== 'function' ) {

    class CaloriesCalculator extends HTMLElement {
        constructor() {
          super();
          this._onDocumentClick = this._onDocumentClick.bind(this);
          this._onCalculateClick = this._onCalculateClick.bind(this);
          this._dropdownClickHandlers = new Map();
          this._optionClickHandlers = new Map();
        }
    
        connectedCallback() {
          this._setupDropdowns();
          this._wireCalculateButton();
          document.addEventListener('click', this._onDocumentClick);
        }
    
        disconnectedCallback() {
          document.removeEventListener('click', this._onDocumentClick);
          this._teardownDropdowns();
          this._unwireCalculateButton();
        }
    
        _setupDropdowns() {
          this._dropdowns = Array.from(this.querySelectorAll('.wrapper-dropdown'));
    
          for (const dd of this._dropdowns) {
            const onDDClick = (evt) => {
              evt.stopPropagation();
              if (dd.classList.contains('active')) {
                this._handleDropdown(dd, false);
              } else {
                this._closeAllDropdowns();
                this._handleDropdown(dd, true);
              }
            };
    
            dd.addEventListener('click', onDDClick);
            this._dropdownClickHandlers.set(dd, onDDClick);
    
            const options = dd.querySelectorAll('li.item');
            const onClicks = [];
            for (const opt of options) {
              const onOptClick = (evt) => {
                evt.stopPropagation();
                const display = dd.querySelector('.selected-display');
                if (display) {
                  display.textContent = (opt.textContent || '').trim();
                  dd.dataset.value = display.textContent;
                }
                this._handleDropdown(dd, false);
              };
              opt.addEventListener('click', onOptClick);
              onClicks.push([opt, onOptClick]);
            }
            this._optionClickHandlers.set(dd, onClicks);
          }
        }
    
        _teardownDropdowns() {
          if (!this._dropdowns) return;
          for (const dd of this._dropdowns) {
            const hd = this._dropdownClickHandlers.get(dd);
            if (hd) dd.removeEventListener('click', hd);
    
            const optionPairs = this._optionClickHandlers.get(dd) || [];
            for (const [opt, h] of optionPairs) opt.removeEventListener('click', h);
          }
          this._dropdownClickHandlers.clear();
          this._optionClickHandlers.clear();
          this._dropdowns = [];
        }
    
        _onDocumentClick(evt) {
          const path = evt.composedPath ? evt.composedPath() : [];
          if (!path.includes(this)) this._closeAllDropdowns();
        }
    
        _closeAllDropdowns() {
          if (!this._dropdowns) return;
          for (const dd of this._dropdowns) this._handleDropdown(dd, false);
        }
    
        _handleDropdown(dropdownEl, open) {
          const arrow = dropdownEl.querySelector('.toggle__icon');
          if (open) {
            if (arrow) arrow.classList.add('rotated');
            dropdownEl.classList.add('active');
          } else {
            if (arrow) arrow.classList.remove('rotated');
            dropdownEl.classList.remove('active');
          }
        }
    
        _wireCalculateButton() {
          this._calcBtn = this.querySelector('#calculate');
          if (this._calcBtn) this._calcBtn.addEventListener('click', this._onCalculateClick);
    
          const inputs = this.querySelectorAll('input[type="number"]');
          for (const i of inputs) {
            i.addEventListener('keydown', (e) => {
              if (e.key === 'Enter') this._onCalculateClick();
            });
          }
        }
    
        _unwireCalculateButton() {
          if (this._calcBtn) this._calcBtn.removeEventListener('click', this._onCalculateClick);
        }
    
        _onCalculateClick() {
          const weight = this._readNumber('#weight');
          const height = this._readNumber('#height');
          const age = this._readNumber('#age');
    
          const ddGender = this._dropdowns?.[0];
          const ddActivity = this._dropdowns?.[1];
          const ddGoal = this._dropdowns?.[2];
    
          const genderText = this._readDropdown(ddGender);
          const activityText = this._readDropdown(ddActivity);
          const goalText = this._readDropdown(ddGoal);
    
          if (!this._validateInputs(weight, height, age, genderText, activityText, goalText)) {
            return;
          }
    
          const gender = this._mapGender(genderText);
          const activity = this._mapActivity(activityText);
          const goal = this._mapGoal(goalText);
    
          const heightM = height / 100;
          const bmi = weight / (heightM * heightM);
    
          let bmr = 10 * weight + 6.25 * height - 5 * age + (gender === 'male' ? 5 : -161);
          let calories = bmr * activity;
          if (goal === 'lose') calories -= 500;
          if (goal === 'gain') calories += 500;
    
          let protein = goal === 'gain' ? weight * 2.0 : weight * 1.6;
          const creatine = 3; 
    
          const resultBox = this.querySelector('.result');
          if (resultBox) resultBox.style.display = 'block'; 
    
          const resultRoot = this.querySelector('#result') || this.querySelector('#results');
          if (resultRoot) {
            const bmiEl = resultRoot.querySelector('.bmi_value');
            const calEl = resultRoot.querySelector('.calories_value');
            const protEl = resultRoot.querySelector('.protein_value');
            const creaEl = resultRoot.querySelector('.creatine_value');
    
            if (bmiEl) bmiEl.textContent = bmi.toFixed(1);
            if (calEl) calEl.textContent = String(Math.round(calories)) + ' kcal';
            if (protEl) protEl.textContent = protein.toFixed(1) + ' g/zi';
            if (creaEl) creaEl.textContent = String(creatine) + ' g/zi';
    
            const bmiContainer = resultRoot.querySelector('.bmi') || resultRoot;
            let statusEl = resultRoot.querySelector('.bmi_status');
            const statusInfo = this._bmiStatus(bmi);
    
            if (!statusEl) {
              statusEl = document.createElement('span');
              statusEl.className = 'bmi_status tag';
              if (bmiEl && bmiEl.parentElement === bmiContainer) {
                bmiEl.insertAdjacentElement('afterend', statusEl);
              } else {
                bmiContainer.appendChild(statusEl);
              }
            }

            statusEl.classList.remove('status-under', 'status-normal', 'status-over', 'status-obese');
            statusEl.classList.add(statusInfo.className);
            statusEl.textContent = statusInfo.label;
          }

          this.dispatchEvent(new CustomEvent('calorie-calc', {
            bubbles: true,
            detail: {
              bmi: Number(bmi.toFixed(1)),
              bmiStatus: this._bmiStatus(bmi).label,
              calories: Math.round(calories),
              protein: Number(protein.toFixed(1)),
              creatine: creatine,
              gender: gender,
              activity: activity,
              goal: goal
            }
          }));
        }
    
        _readNumber(selector) {
          const el = this.querySelector(selector);
          if (!el) return NaN;
          const v = parseFloat(el.value);
          return v;
        }
    
        _readDropdown(dd) {
          if (!dd) return '';
          const v = dd.dataset.value || dd.querySelector('.selected-display')?.textContent || '';
          return v.trim();
        }
    
        _validateInputs(weight, height, age, genderText, activityText, goalText) {
          const invalids = [];
          if (!(weight > 0)) invalids.push('greutate');
          if (!(height > 0)) invalids.push('înălțime');
          if (!(age > 0)) invalids.push('vârstă');
          if (!this._mapGender(genderText)) invalids.push('sex');
          if (!this._mapActivity(activityText)) invalids.push('activitate');
          if (!this._mapGoal(goalText)) invalids.push('obiectiv');
    
          if (invalids.length) {
            console.warn('Câmpuri invalide/lipsă:', invalids.join(', '));
            return false;
          }
          return true;
        }
    
        _norm(s) {
          return (s || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim();
        }
    
        _mapGender(text) {
          const t = this._norm(text);
          if (t === 'male' || t === 'masculin' || t === 'barbat' || t === 'bărbat') return 'male';
          if (t === 'female' || t === 'feminin' || t === 'femeie') return 'female';
          return '';
        }
    
        _mapActivity(text) {
          const t = this._norm(text);
          if (t.includes('sedentar')) return 1.2;
          if (t.includes('usoara') || t.includes('usoar') || t.includes('ușoar')) return 1.375;
          if (t.includes('moderata') || t.includes('moderat')) return 1.55;
          if (t.includes('extrem')) return 1.9;
          if (t.includes('intens')) return 1.725;
          return 0;
        }
    
        _mapGoal(text) {
          const t = this._norm(text);
          if (t.includes('scadere') || t.includes('scădere') || t.includes('slabire')) return 'lose';
          if (t.includes('mentin') || t.includes('mențin')) return 'maintain';
          if (t.includes('crestere') || t.includes('creștere') || t.includes('masa') || t.includes('masă')) return 'gain';
          return '';
        }
    
        _bmiStatus(bmi) {
          if (bmi < 18.5) return { label: 'Subponderal', className: 'status-under' };
          if (bmi < 25)   return { label: 'Normal',       className: 'status-normal' };
          if (bmi < 30)   return { label: 'Supraponderal',className: 'status-over' };
          return { label: 'Obezitate', className: 'status-obese' };
        }
    
        // Public helper
        get selections() {
          const out = {};
          const labels = this.querySelectorAll('.input .dropdown-label');
          (this._dropdowns || []).forEach((dd, i) => {
            const key = (labels[i]?.textContent || `dropdown_${i + 1}`).trim();
            const val = dd.dataset.value || dd.querySelector('.selected-display')?.textContent || '';
            out[key] = val.trim();
          });
          return out;
        }
    }

    if (typeof customElements.get("calories-calculator") == "undefined") {
        customElements.define("calories-calculator", CaloriesCalculator);
    }

}